import React, { useState, useEffect, useRef } from 'react';
import {
  Path
} from 'slate';
import { setAttr } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import applicationEventCodes from 'application-event-codes';
import internalEventCodes from 'event-system/codes/internal';

import useOnClickOutside from 'generics/hooks/on-click-outside';
import {
  displayElementAroundCursor
} from 'utilities/element-position';
import {
  findIndexByPropertyValue,
  convertToOptionsCollection
} from 'utilities/collection';

import HelptapEditor from '../helpers';
import { toggleBlock } from '../3rd-party/rich-text';

import EditorActions from '../editor-actions';
import elementTypes from '../elements/types';

import AddActionLabelView from './add-action-label-view';

import {
  getSelectedActionIndex,
  filterInvisibleActions,
  filterInvisibleFavouritePageNodes,
  filterActionsByDisabledAndEnabledElementsParameters,
  filterFavouritePageNodesByDisabledAndEnabledElementsParameters,
  filterTemplatesBySearchTerm,
  createFavouritePageNodes
} from './utilities';

import { createHeaderAction } from './site-nav';
import { createIconAction } from './icon';

import './index.scss';

const FILE_BUTTON_TYPE = 'file';

function EditorOptions(props) {

  const editor = props.editor;

  const actions = [{
    iconName: 'ask-ai-icon',
    label: 'Ask AI',
    description: 'Ask AI for content, suggestions',
    onClick() {
      handleAskAIButtonClick();
    },
    type: 'ask-ai'
  }, {
    iconName: 'text-icon',
    label: 'Text',
    description: 'Just start writing plain text',
    onClick() {
      genericButtonClickHandler(handleInsertParagraphButtonClick);
    },
    type: elementTypes.DEFAULT
  }, {
    iconName: 'heading-icon',
    label: 'H1',
    description: 'Big heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_ONE));
    },
    type: elementTypes.HEADING_ONE
  }, {
    iconName: 'heading-icon',
    label: 'H2',
    description: 'Medium heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_TWO));
    },
    type: elementTypes.HEADING_TWO
  }, {
    iconName: 'heading-icon',
    label: 'H3',
    description: 'Small heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_THREE));
    },
    type: elementTypes.HEADING_THREE
  }, {
    iconName: 'heading-icon',
    label: 'H4',
    description: 'Smaller heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_FOUR));
    },
    type: elementTypes.HEADING_FOUR
  }, {
    iconName: 'heading-icon',
    label: 'H5',
    description: 'Smallest heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_FIVE));
    },
    type: elementTypes.HEADING_FIVE
  }, {
    iconName: 'heading-icon',
    label: 'H6',
    description: 'Tiny heading',
    onClick() {
      genericButtonClickHandler(handleInsertHeaderBlockClick.bind(null, elementTypes.HEADING_SIX));
    },
    type: elementTypes.HEADING_SIX
  }, {
    iconName: 'attachment-icon',
    label: 'Upload',
    description: 'Image, Video, Document...',
    keywords: ['image', 'video', 'document', 'file'],
    onClick() {
      genericButtonClickHandler(handleFileUploadOptionClick);
    },
    type: elementTypes.HT_FILE
  }, {
    iconName: 'attachment-icon',
    label: 'Gallery',
    description: 'Add an image gallery',
    keywords: ['gallery', 'images', 'photos'],
    onClick() {
      genericButtonClickHandler(handleAddGalleryButtonClick);
    },
    type: elementTypes.GALLERY
  }, {
    iconName: 'attachment-icon',
    label: 'Carousel',
    description: 'Add an image carousel',
    keywords: ['carousel', 'slider', 'images', 'photos'],
    onClick() {
      genericButtonClickHandler(handleAddCarouselButtonClick);
    },
    type: elementTypes.CAROUSEL
  }, {
    iconName: 'button-icon',
    label: 'Button',
    description: 'Add a simple button',
    onClick() {
      genericButtonClickHandler(handleInsertButtonClick);
    },
    type: elementTypes.BUTTON
  }, {
    iconName: 'authenticator-button-icon',
    label: 'Authorize Button',
    description: 'Add a authorize button',
    onClick() {
      genericButtonClickHandler(handleInsertAuthenticatorButtonClick);
    },
    type: elementTypes.AUTHENTICATOR_BUTTON
  }, {
    iconName: 'url-embed-icon',
    label: 'Embed',
    description: 'YouTube, Tweet, Maps, Doc...',
    keywords: ['url', 'image', 'video', 'document', 'file'],
    onClick() {
      genericButtonClickHandler(handleEmbedURLButtonClick);
    },
    type: elementTypes.URL_EMBED
  }, {
    iconName: 'divider-icon',
    label: 'Divider',
    description: 'Divide blocks',
    onClick() {
      genericButtonClickHandler(handleDividerButtonClick);
    },
    type: elementTypes.DIVIDER
  }, {
    iconName: 'bullet-list-icon',
    label: 'Bullet List',
    description: 'Bullet list',
    onClick() {
      genericButtonClickHandler(handleBulletListButtonClick);
    },
    type: elementTypes.BULLETED_LIST
  }, {
    iconName: 'number-list-icon',
    label: 'Number list',
    description: 'Number list',
    onClick() {
      genericButtonClickHandler(handleNumberListButtonClick);
    },
    type: elementTypes.NUMBERED_LIST
  }, {
    iconName: 'check-list-icon',
    label: 'To-do',
    description: 'Todo list',
    onClick() {
      genericButtonClickHandler(handleCheckListButtonClick);
    },
    type: elementTypes.CHECK_LIST_ITEM
  }, {
    iconName: 'toggle-list-item-icon',
    label: 'Toggle list',
    description: 'Add a toggle list',
    onClick(data) {
      genericButtonClickHandler(handleAddToggleListItemButtonClick, elementTypes.TOGGLE_LIST_ITEM, false, data);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.TOGGLE_LIST_ITEM
  }, {
    iconName: 'quote-icon',
    label: 'Quote',
    description: 'Add quote',
    onClick() {
      genericButtonClickHandler(handleQuoteButtonClick);
    },
    type: elementTypes.BLOCK_QUOTE
  }, {
    iconName: 'event-icon',
    label: 'Event',
    description: 'Create Event',
    onClick() {
      genericButtonClickHandler(handleEventButtonClick);
    },
    isVisible(editor) {

      const possibleToCreateLiveStreaming = application.accessController.canCreateLiveStreaming();

      if (possibleToCreateLiveStreaming === false) {
        return false;
      }

      return HelptapEditor.element.countElement(editor, elementTypes.HT_STARTUP_EVENT_TEMPLATE) === 0;
    },
    type: elementTypes.HT_STARTUP_EVENT
  }, {
    iconName: 'event-icon',
    label: 'Columns',
    description: 'Add two column',
    onClick() {

      genericButtonClickHandler(handleTwoColumnButtonClick.bind(null, 2));

    },
    isVisible() {
      return (props.level < 1 && HelptapEditor.layout.isInsideLayout(editor, getTargetBlockNodePath()) === false);
    },
    type: elementTypes.COLUMNS
  }, {
    iconName: 'event-icon',
    label: 'Layout',
    description: 'Arrange blocks in a row or stack',
    keywords: ['layout', 'row', 'stack', 'side by side', 'columns', 'grid', 'flex'],
    onClick() {
      genericButtonClickHandler(handleInsertLayoutButtonClick);
    },
    isVisible() {
      return HelptapEditor.layout.canInsertAt(editor, getTargetBlockNodePath());
    },
    type: elementTypes.LAYOUT
  }, createIconAction(editor, getTargetBlockNodePath, genericButtonClickHandler), createHeaderAction(editor, getTargetBlockNodePath, genericButtonClickHandler), {
    iconName: 'form-icon',
    label: 'Form',
    description: 'Add form',
    onClick() {
      genericButtonClickHandler(handleAddHTFormButtonClick);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.FORM
  }, {
    iconName: 'form-icon',
    label: 'Signup form',
    description: 'Add signup form',
    onClick() {
      genericButtonClickHandler(handleAddSignupFormButtonClick);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.SIGNUP_FORM
  }, {
    iconName: 'import-html-icon',
    label: 'Import HTML',
    description: 'Import HTML file',
    onClick() {
      genericButtonClickHandler(handleAddImportHTMLButtonClick);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.HTML_IMPORTER
  }, {
    iconName: 'source-code-region-icon',
    label: 'Code',
    description: 'Embed HTML/CSS/JS code',
    onClick() {
      genericButtonClickHandler(handleAddSourceCodeRegionButtonClick);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.SOURCE_CODE_REGION
  }, {
    iconName: 'table-icon',
    label: 'Table',
    description: 'Add table',
    keywords: ['table'],
    onClick() {
      genericButtonClickHandler(handleAddTableButtonClick);
    },
    type: elementTypes.TABLE
  }, {
    iconName: 'product-icon',
    label: 'Product',
    description: 'Add product',
    keywords: ['product'],
    onClick(data) {
      genericButtonClickHandler(handleAddProductButtonClick, elementTypes.PRODUCT, false, data);
    },
    type: elementTypes.PRODUCT,
    expandable: true,
    optionsGetter() {

      const products = application.marketProductsManager.getAll();

      if (products.length === 0) {
        return [{
          label: 'Create product',
          value: null,
          labelView: new AddActionLabelView({ label: 'Create product' })
        }];
      }

      return convertToOptionsCollection(products, 'name', '_id');
    }
  }, {
    iconName: 'data-collection-icon',
    label: 'Database',
    description: 'Add database as block',
    onClick(data) {
      genericButtonClickHandler(handleAddDataCollectionButtonClick, elementTypes.DATA_COLLECTION, false, data);
    },
    isVisible(editor) {

      return application.dataManagers.get('DataCollectionsManager').getAll().length > 0;

    },
    type: elementTypes.DATA_COLLECTION,
    expandable: true,
    optionsGetter() {
      return convertToOptionsCollection(application.dataManagers.get('DataCollectionsManager').getAll(), 'name', '_id');
    }
  }, {
    iconName: 'directory-icon',
    label: 'Directory',
    description: 'Add database directory as block',
    onClick(data) {
      genericButtonClickHandler(handleAddDirectoryButtonClick, elementTypes.DIRECTORY, false, data);
    },
    isVisible(editor) {

      return application.dataManagers.get('DataCollectionsManager').getAll().length > 0;

    },
    type: elementTypes.DIRECTORY,
    expandable: true,
    optionsGetter() {
      return convertToOptionsCollection(application.dataManagers.get('DataCollectionsManager').getAll(), 'name', '_id');
    }
  }, {
    iconName: 'canvas-icon',
    label: 'Canvas',
    description: 'Add a canvas',
    onClick() {
      genericButtonClickHandler(handleAddCanvasButtonClick);
    },
    type: elementTypes.CANVAS
  }, {
    iconName: 'canvas-icon',
    label: 'Video Canvas',
    description: 'Add a video canvas',
    onClick() {
      genericButtonClickHandler(handleAddVideoCanvasButtonClick);
    },
    type: elementTypes.VIDEO_CANVAS
  }, {
    iconName: 'canvas-icon',
    label: 'Signature',
    description: 'Add signature block',
    onClick() {
      genericButtonClickHandler(handleAddSignaturePanelButtonClick);
    },
    type: elementTypes.SIGNATURE_PANEL,
    isVisible() {
      return props.parentEntityType === 'PAGE';
    }
  }, {
    iconName: 'canvas-icon',
    label: 'Last updated',
    description: 'Display last updated date/time',
    onClick() {
      genericButtonClickHandler(handleAddLastUpdatedButtonClick);
    },
    type: elementTypes.LAST_UPDATED,
    isVisible() {
      return props.parentEntityType === 'PAGE';
    }
  }, {
    iconName: 'canvas-icon',
    label: 'Author',
    description: 'Display page author',
    onClick() {
      genericButtonClickHandler(handleAddPageAuthorButtonClick);
    },
    type: elementTypes.PAGE_AUTHOR,
    isVisible() {
      return props.parentEntityType === 'PAGE';
    }
  }, {
    label: 'Dynamic content',
    description: 'AI-generated content for readers',
    onClick() {
      genericButtonClickHandler(handleAddAIAssistantButtonClick);
    },
    type: elementTypes.AI_ASSISTANT,
    isVisible() {
      return props.parentEntityType === 'PAGE' || props.parentEntityType === 'CAMPAIGN_STEP';
    }
  }];

  actions.push({
    iconName: 'calendar-icon',
    label: 'Member calendar',
    description: 'Add member calendar',
    onClick(data) {
      genericButtonClickHandler(handleAddMemberCalendarButtonClick, elementTypes.MEMBER_CALENDAR, false, data);
    },
    isVisible(editor) {

      return (props.level < 1);

    },
    type: elementTypes.MEMBER_CALENDAR,
    expandable: true,
    optionsGetter() {
      const members = getMembersOpenForMeetings();
      if (members.length === 0) {
        return [{
          label: 'Create calendar',
          value: null,
          labelView: new AddActionLabelView({ label: 'Create calendar' })
        }];
      }
      return convertToOptionsCollection(members, 'fullName', '_id');
    }
  });

  const visibleActions = filterInvisibleActions(actions, editor);
  const matchingActions = filterActionsByDisabledAndEnabledElementsParameters(visibleActions, props.disabledElements, props.enabledElements, props.searchTerm);

  const [rootState, setRootState] = useState({
    favouritePageNodes: createFavouritePageNodes([...application.favouritePageNodesManager.getAll(),
      ...application.favouritePageNodesManager.getAllPublicNodes()
    ])
  });

  const favouritePageNodes = rootState.favouritePageNodes;
  const visibleFavouritePageNodes = filterInvisibleFavouritePageNodes(favouritePageNodes, actions, editor);
  const matchingFavouritePageNodes = filterFavouritePageNodesByDisabledAndEnabledElementsParameters(visibleFavouritePageNodes, props.disabledElements, props.enabledElements, props.searchTerm);
  const matchingTemplates = filterTemplatesBySearchTerm(props.templates, props.searchTerm);

  const viewportReference = props.viewportReference;
  const insertOptions = {
    at: props.insertPoint
  };

  const menuReference = useRef(null);

  useOnClickOutside(menuReference, props.onClose);

  useEffect(function onLoad() {

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function positionToolbar() {

    tryPositionActionsMenu();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuReference.current]);

  useEffect(function onApplySelectedActionChange() {

    if (props.applySelectedAction === false) {
      return;
    }

    applySelectedAction();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.applySelectedAction]);

  useEffect(function onCloseMenuChange() {

    if ([...matchingActions, ...matchingFavouritePageNodes, ...matchingTemplates].length !== 0) {
      return;
    }

    const timerReference = setTimeout(function someTimeLater() {

      props.onClose();

    }, 500);

    return function clearTimer() {
      clearTimeout(timerReference);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingActions.length, matchingFavouritePageNodes.length, matchingTemplates.length]);

  useEffect(function onSearchTermChange() {

    tryPositionActionsMenu();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.searchTerm]);

  function getMembersOpenForMeetings() {

    return application.startupModule.members.getMembers().filter(function openForAppointmentMembersFilter(member) {
      return member.openForMeetings === true;
    });

  }

  function applySelectedAction() {

    const actionIndex = getSelectedActionIndex(actions, props.actionIndex);

    if (actionIndex === -1) {
      return;
    }

    const selectedAction = matchingActions[actionIndex];

    if (selectedAction === void 0) {
      return;
    }

    if (selectedAction.buttonType === FILE_BUTTON_TYPE) {
      selectedAction.applied = true;
    } else {
      selectedAction.onClick();
    }

  }

  function tryPositionActionsMenu() {

    const element = menuReference.current;

    if (element === null) {
      return;
    }

    const viewportElement = viewportReference.current;
    const verticalOffset = 0;
    const horizontalOffset = 5;
    const startSide = 'right';
    const placeOnCenter = false;
    const positionableSides = ['bottom', 'right', 'left'];

    displayElementAroundCursor(element, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

    setAttr(element, {
      style: {
        height: 'auto'
      }
    });

  }

  function handleAddDataCollectionButtonClick(type, selectedOption) {

    HelptapEditor.dataCollection.insert(editor, { collectionId: selectedOption.value });

  }

  function handleAddDirectoryButtonClick(type, selectedOption) {

    HelptapEditor.directory.insert(editor, props.insertPoint.path, selectedOption.value);

  }

  function handleAskAIButtonClick() {

    HelptapEditor.deleteBackward(editor, props.searchTerm.length + 1);
    application.assistantManager.show({
      text: '',
      options: {
        skipTrainingData: true,
        historyLimit: 0
      }
    });

  }

  function genericButtonClickHandler(nextHandler, type, cancelAction, data) {

    props.onClose();

    if (cancelAction === true) {
      return;
    }

    if (props.caller === EditorOptions.callers.KEYBOARD) {
      HelptapEditor.deleteBackward(editor, props.searchTerm.length + 1);
    }

    nextHandler(type, data);

  }

  function handleInsertParagraphButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'paragraph');
    }

    HelptapEditor.insertParagraph(editor, insertOptions);

  }

  async function handleAddCanvasButtonClick() {

    toggleOrInsert(HelptapEditor.canvas, {
      parentEntityType: props.parentEntityType,
      parentEntityId: props.parentEntityId
    });

  }

  async function handleAddVideoCanvasButtonClick() {

    toggleOrInsert(HelptapEditor.videoCanvas, {
      parentEntityType: props.parentEntityType,
      parentEntityId: props.parentEntityId
    });

  }

  function handleAddSignaturePanelButtonClick() {

    toggleOrInsert(HelptapEditor.signaturePanel, {
      pageId: props.parentEntityId
    });

  }

  function handleAddLastUpdatedButtonClick() {

    toggleOrInsert(HelptapEditor.lastUpdated, {
      lastUpdatedAt: Date.now()
    });

  }

  function handleAddPageAuthorButtonClick() {

    const currentUser = application.getUser();

    toggleOrInsert(HelptapEditor.pageAuthor, {
      author: {
        _id: currentUser._id,
        fullName: currentUser.fullName,
        profilePicture: currentUser.profilePicture
      }
    });

  }

  function handleAddAIAssistantButtonClick() {

    toggleOrInsert(HelptapEditor.aiAssistant, {

    });

  }

  async function handleAddImportHTMLButtonClick() {

    const response = await application.domUtility.askFile({ type: 'text/html,*.html' });

    if (response.cancelled === true) {
      return;
    }

    toggleOrInsert(HelptapEditor.htmlImporter, { file: response.file });

  }

  async function handleAddSourceCodeRegionButtonClick() {

    toggleOrInsert(HelptapEditor.sourceCodeRegion);

  }

  async function handleAddToggleListItemButtonClick() {

    toggleOrInsert(HelptapEditor.toggleListItem, {}, { at: props.insertPoint });

  }

  async function handleAddTableButtonClick() {

    toggleOrInsert(HelptapEditor.table, {}, { at: props.insertPoint });
  }

  async function handleAddProductButtonClick(type, selectedOption) {

    if (selectedOption.value === null) {
      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: '/products?new=true',
      });
      return;
    }

    const nodeOptions = {
      options: {
        productId: selectedOption.value
      }
    };

    if (import.meta.env.NODE_ENV === 'development') {
      toggleOrInsert(HelptapEditor.product, nodeOptions, { at: props.insertPoint });
      return;
    }

    const result = await serverInterface.startup.users.banking.getBankAccountStatusByStartupId();

    if (result.status !== 'VERIFIED') {
      application.applicationLevelDialogs.displayBankAccountVerifier(result);
      return;
    }

    toggleOrInsert(HelptapEditor.product, nodeOptions, { at: props.insertPoint });

  }

  function handleInsertHeaderBlockClick(type) {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    const blockOptions = {
      properties: {}
    };

    if (startOfTheLine === true) {
      return toggleBlock(editor, type, blockOptions);
    }

    HelptapEditor.heading.insert(editor, type, {
      at: {
        path: determineNextNodePath()
      }
    });

  }

  function getTargetBlockNodePath() {

    return HelptapEditor.getTargetBlockNodePath(editor, props.insertPoint.path);

  }

  function determineNextNodePath() {

    const targetNodePath = getTargetBlockNodePath();

    if (targetNodePath === null) {
      return null;
    }

    return Path.next(targetNodePath);

  }

  function toggleOrInsert(nodeUtilities, options, editorOptions) {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);
    const targetNodePath = getTargetBlockNodePath();
    const nextNodePath = determineNextNodePath();

    if (startOfTheLine === true) {
      return nodeUtilities.toggle(editor, options, editorOptions, targetNodePath, nextNodePath);
    }

    nodeUtilities.insert(editor, options, editorOptions, targetNodePath, nextNodePath);

  }

  function handleInsertButtonClick() {

    toggleOrInsert(HelptapEditor.button);

  }

  function handleInsertAuthenticatorButtonClick() {

    toggleOrInsert(HelptapEditor.authenticatorButton);

  }

  function handleEmbedURLButtonClick() {

    const urlEmbedOptions = {
      url: ''
    };
    const nodeOptions = {
      ...insertOptions,
      urlEmbedOptions,
      children: [{ text: '' }]
    };

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'url-embed', nodeOptions);
    }

    HelptapEditor.urlEmbed.insert(editor, determineNextNodePath());

  }

  function handleEventButtonClick() {

    HelptapEditor.insertHTStartupEventTemplate(editor, {
      postId: props.parentEntityId,
      insertPoint: props.insertPoint
    });

  }

  async function handleFileUploadOptionClick(fileType) {

    const response = await application.domUtility.askAnyTypeFiles();

    if (response.cancelled === true) {
      return;
    }

    const htmlFiles = response.files.filter((file) => file.type === 'text/html');
    const otherFiles = response.files.filter((file) => file.type !== 'text/html');

    for (let file of htmlFiles) {
      HelptapEditor.sourceCodeRegion.insertHTMLFile(editor, file);
    }
    HelptapEditor.file.insertMany(editor, otherFiles);

  }

  async function handleAddGalleryButtonClick() {

    const response = await application.domUtility.askFiles({ type: 'image/*', limit: 10 });

    if (response.cancelled === true) {
      return;
    }

    HelptapEditor.gallery.insert(editor, response.files);

  }

  async function handleAddCarouselButtonClick() {

    const response = await application.domUtility.askFiles({ type: 'image/*', limit: 10 });

    if (response.cancelled === true) {
      return;
    }

    HelptapEditor.carousel.insert(editor, response.files);

  }

  function handleDividerButtonClick() {

    const dividerOptions = {
      ...insertOptions,
      padding: [0, 5, 0, 5]
    };

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      toggleBlock(editor, 'divider', dividerOptions);
    } else {
      HelptapEditor.insertBlock(editor, 'divider', dividerOptions);
    }

    HelptapEditor.tryInsertNewLine(editor);

  }

  function handleBulletListButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'bulleted-list');
    }

    HelptapEditor.insertListBlock(editor, 'bulleted-list', {
      ...insertOptions,
    });

  }

  function handleNumberListButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'numbered-list');
    }

    HelptapEditor.insertListBlock(editor, 'numbered-list', {
      ...insertOptions,
    });

  }

  function handleCheckListButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'check-list-item');
    }

    HelptapEditor.insertCheckListBlock(editor, {
      ...insertOptions,
      padding: [5, 5, 5, 0],
      margin: [5, 5, 5, 0]
    });

  }

  function handleQuoteButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return toggleBlock(editor, 'block-quote');
    }

    HelptapEditor.insertBlock(editor, 'block-quote', {
      ...insertOptions
    });

  }

  function handleTwoColumnButtonClick() {

    const targetNodePath = getTargetBlockNodePath();
    const nextNodePath = determineNextNodePath();

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    if (startOfTheLine === true) {
      return HelptapEditor.swtichToColumnsLayout(editor, targetNodePath);
    }

    HelptapEditor.insertColumnsLayout(editor, nextNodePath);

  }

  function handleInsertLayoutButtonClick() {

    const startOfTheLine = HelptapEditor.isPointAtStartOfTheLine(editor, props.insertPoint);

    HelptapEditor.layout.insert(editor, getTargetBlockNodePath(), startOfTheLine);

  }

  function handleLiveStreamButtonClick() {

    toggleOrInsert(HelptapEditor.singleProducerMeeting, {
      attributes: {
        pageId: props.parentEntityId
      }
    });

  }

  function handleAddHTFormButtonClick() {

    toggleOrInsert(HelptapEditor.form);

  }

  function handleAddSignupFormButtonClick() {

    toggleOrInsert(HelptapEditor.form, {
      type: elementTypes.SIGNUP_FORM
    });

  }

  function handleAddMemberCalendarButtonClick(type, selectedOption) {

    if (selectedOption.value === null) {
      application.eventSystem.internal.emit(applicationEventCodes.DISPLAY_PROFILE_DIALOG, { defaultActiveSectionName: 'Availability' });
      return;
    }

    toggleOrInsert(HelptapEditor.memberCalendar, { options: { memberId: selectedOption.value } });

  }

  function handleFavouritePageNodeClick(favouritePageNode, cancelAction) {

    props.onClose();

    if (cancelAction === true) {
      return;
    }

    if (props.caller === EditorOptions.callers.KEYBOARD) {
      HelptapEditor.deleteBackward(editor, props.searchTerm.length + 1);
    }

    HelptapEditor.duplicate.toggle(editor, {
      favouritePageNode,
      pageId: props.parentEntityId
    });

  }

  function handleFavouritePageNodeRemoveClick(favouritePageNodeId) {

    setRootState(function updateRootState(rootState) {

      const favouritePageNodes = rootState.favouritePageNodes;

      const favouritePageNodeIndex = findIndexByPropertyValue(favouritePageNodes, '_id', favouritePageNodeId);

      if (favouritePageNodeIndex !== -1) {
        favouritePageNodes.splice(favouritePageNodeIndex, 1);
      }

      return {
        ...rootState,
        favouritePageNodes: [...favouritePageNodes]
      };

    });

  }

  function handleTemplateClick(template, cancelAction) {

    props.onClose();

    if (cancelAction === true) {
      return;
    }

    if (props.caller === EditorOptions.callers.KEYBOARD) {
      HelptapEditor.deleteBackward(editor, props.searchTerm.length + 1);
    }

    HelptapEditor.templateCloner.toggle(editor, {
      template,
      parentEntityId: props.parentEntityId,
      parentEntityType: props.parentEntityType
    });

  }

  function renderEditorActions(actions) {

    if (actions.length > 0) {
      const actionIndex = getSelectedActionIndex(actions, props.actionIndex);

      actions[actionIndex].selected = true;
    }

    const editorActionsProperties = {
      actionGroups: [{
        name: 'ADD BLOCK',
        actions
      }]
    };

    if (props.displayFavouriteBlocks !== false) {
      editorActionsProperties.actionGroups.push({
        name: 'FAVOURITE BLOCKS',
        actions: matchingFavouritePageNodes,
        onClick: handleFavouritePageNodeClick,
        onRemove: handleFavouritePageNodeRemoveClick
      });
    }

    return <EditorActions {...editorActionsProperties} />;

  }

  function renderMain() {

    const mainAttributes = {
      className: 'overlay-menu-main editor-tool-container'
    };

    const menuContainerAttributes = {
      className: 'overlay-menu-container',
      ref: menuReference
    };

    if ([...matchingActions, ...matchingFavouritePageNodes, ...matchingTemplates].length === 0) {
      return <React.Fragment></React.Fragment>;
    }

    return <div {...mainAttributes}>
      <div {...menuContainerAttributes}>
        {renderEditorActions(matchingActions)}
      </div>
    </div>;

  }

  return renderMain();

}

EditorOptions.callers = {
  MOUSE: 'MOUSE',
  KEYBOARD: 'KEYBOARD'
};

export default EditorOptions;